import { z } from 'zod';
import { colourSchema } from './classification';
import { currencyCodeSchema } from './common';
import { lutsSchema } from './exports';
/**
 * Everything an admin can configure for an org, stored as one JSON blob on
 * `orgs.settings_json`.
 *
 * Why a blob rather than columns: these are settings, not records. Nothing
 * joins to them, nothing filters by them, and every one of them is read as a
 * whole when a document is priced. A column per setting is a migration per
 * setting, and this file grows every release.
 *
 * The read path must parse defensively - on MariaDB Drizzle's `json()` is
 * LONGTEXT and mysql2 returns a raw string. `parseOrgSettings` below is the
 * only supported way to read it.
 */
// ── Indian state codes ────────────────────────────────────────────────────
/**
 * State codes, used for the GST place-of-supply comparison.
 *
 * The comparison that decides CGST+SGST vs IGST is on THIS code, never on the
 * address text a user typed. "Tamil Nadu", "TAMILNADU" and "tamilnadu" are one
 * state, and a string compare on free text charges half of them the wrong tax.
 *
 * The numeric prefix is the GST state code, which is also the first two digits
 * of every GSTIN issued in that state - so a GSTIN can be checked against the
 * selected state rather than trusted.
 */
export const INDIAN_STATES = [
    { code: 'JK', gstCode: '01', name: 'Jammu and Kashmir' },
    { code: 'HP', gstCode: '02', name: 'Himachal Pradesh' },
    { code: 'PB', gstCode: '03', name: 'Punjab' },
    { code: 'CH', gstCode: '04', name: 'Chandigarh' },
    { code: 'UT', gstCode: '05', name: 'Uttarakhand' },
    { code: 'HR', gstCode: '06', name: 'Haryana' },
    { code: 'DL', gstCode: '07', name: 'Delhi' },
    { code: 'RJ', gstCode: '08', name: 'Rajasthan' },
    { code: 'UP', gstCode: '09', name: 'Uttar Pradesh' },
    { code: 'BR', gstCode: '10', name: 'Bihar' },
    { code: 'SK', gstCode: '11', name: 'Sikkim' },
    { code: 'AR', gstCode: '12', name: 'Arunachal Pradesh' },
    { code: 'NL', gstCode: '13', name: 'Nagaland' },
    { code: 'MN', gstCode: '14', name: 'Manipur' },
    { code: 'MZ', gstCode: '15', name: 'Mizoram' },
    { code: 'TR', gstCode: '16', name: 'Tripura' },
    { code: 'ML', gstCode: '17', name: 'Meghalaya' },
    { code: 'AS', gstCode: '18', name: 'Assam' },
    { code: 'WB', gstCode: '19', name: 'West Bengal' },
    { code: 'JH', gstCode: '20', name: 'Jharkhand' },
    { code: 'OR', gstCode: '21', name: 'Odisha' },
    { code: 'CG', gstCode: '22', name: 'Chhattisgarh' },
    { code: 'MP', gstCode: '23', name: 'Madhya Pradesh' },
    { code: 'GJ', gstCode: '24', name: 'Gujarat' },
    { code: 'DN', gstCode: '26', name: 'Dadra and Nagar Haveli and Daman and Diu' },
    { code: 'MH', gstCode: '27', name: 'Maharashtra' },
    { code: 'KA', gstCode: '29', name: 'Karnataka' },
    { code: 'GA', gstCode: '30', name: 'Goa' },
    { code: 'LD', gstCode: '31', name: 'Lakshadweep' },
    { code: 'KL', gstCode: '32', name: 'Kerala' },
    { code: 'TN', gstCode: '33', name: 'Tamil Nadu' },
    { code: 'PY', gstCode: '34', name: 'Puducherry' },
    { code: 'AN', gstCode: '35', name: 'Andaman and Nicobar Islands' },
    { code: 'TS', gstCode: '36', name: 'Telangana' },
    { code: 'AP', gstCode: '37', name: 'Andhra Pradesh' },
    { code: 'LA', gstCode: '38', name: 'Ladakh' },
    { code: 'OT', gstCode: '97', name: 'Other Territory' },
];
const STATE_BY_CODE = new Map(INDIAN_STATES.map((s) => [s.code, s]));
export const stateByCode = (code) => STATE_BY_CODE.get(code.trim().toUpperCase());
/**
 * A GSTIN is 15 characters: 2-digit state code, 10-character PAN, an entity
 * digit, a fixed 'Z', and a checksum character.
 *
 * This validates the SHAPE only. The checksum is verified separately by
 * `isValidGstinChecksum` - a typo'd GSTIN that passes the shape check still
 * prints on an invoice, and the customer's accountant is the one who finds out.
 */
export const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;
/**
 * The GSTIN checksum, as published by GSTN.
 *
 * Each of the first 14 characters maps to a value in base 36; positions
 * alternate a weight of 1 and 2; each product is folded (quotient + remainder
 * over 36) and summed; the check character is what brings the total to a
 * multiple of 36.
 */
export function isValidGstinChecksum(gstin) {
    const value = gstin.trim().toUpperCase();
    if (!GSTIN_PATTERN.test(value))
        return false;
    const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let sum = 0;
    for (let i = 0; i < 14; i++) {
        const code = alphabet.indexOf(value[i]);
        if (code === -1)
            return false;
        const product = code * (i % 2 === 0 ? 1 : 2);
        sum += Math.floor(product / 36) + (product % 36);
    }
    const expected = alphabet[(36 - (sum % 36)) % 36];
    return expected === value[14];
}
/** The state a GSTIN was issued in, from its first two digits. */
export function stateOfGstin(gstin) {
    const prefix = gstin.trim().slice(0, 2);
    return INDIAN_STATES.find((s) => s.gstCode === prefix);
}
// ── Tax configuration: the GST switch ─────────────────────────────────────
/**
 * How this org taxes.
 *
 * `none` gives plain named rates ("VAT 20%") applied per line or per document -
 * this works anywhere in the world and is the default.
 *
 * `gst` turns on the Indian regime: GSTIN and GST treatment on every customer
 * and vendor, place of supply on every document, HSN/SAC on every item, and the
 * CGST+SGST vs IGST split. Everything GST adds is additive, which is what makes
 * it a switch rather than a separate product.
 */
export const taxRegimeSchema = z.enum(['none', 'gst']);
export const taxSettingsSchema = z
    .object({
    regime: taxRegimeSchema.default('none'),
    /** The org's own GSTIN. Required before a GST invoice can be issued. */
    gstin: z.string().trim().toUpperCase().optional(),
    /**
     * The state supply is made FROM. The other half of the split decision, and
     * the reason a GSTIN alone is not enough: a business can hold registrations
     * in several states.
     */
    stateCode: z.string().trim().toUpperCase().optional(),
    /**
     * Composition dealers charge no GST and must print a specific declaration
     * instead. Turning it on suppresses tax on every document.
     */
    composition: z.boolean().default(false),
    /** Whether the reverse-charge control appears on documents at all. */
    reverseChargeEnabled: z.boolean().default(false),
    /** Rates are `inclusive` or `exclusive` unless a document says otherwise. */
    defaultTaxMode: z.enum(['exclusive', 'inclusive']).default('exclusive'),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.regime !== 'gst')
        return;
    // These are only required once GST is actually on. Demanding them up front
    // would block an org from saving any settings at all before they have a
    // registration - and plenty of orgs never will.
    if (!value.stateCode) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['stateCode'],
            message: 'Pick the state you are registered in. Without it, GST cannot tell an intra-state sale from an inter-state one.',
        });
    }
    else if (!stateByCode(value.stateCode)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['stateCode'], message: 'Not a recognised Indian state code.' });
    }
    if (value.gstin) {
        if (!isValidGstinChecksum(value.gstin)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['gstin'],
                message: 'That GSTIN fails its checksum - check for a typo. It will print on every invoice you issue.',
            });
        }
        else if (value.stateCode && stateOfGstin(value.gstin)?.code !== value.stateCode) {
            // Catches the common paste-the-wrong-branch mistake, which otherwise
            // shows up as every invoice carrying the wrong state's registration.
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['gstin'],
                message: `That GSTIN was issued in ${stateOfGstin(value.gstin)?.name ?? 'another state'}, which is not the state selected.`,
            });
        }
    }
});
// ── The rest of the settings blob ─────────────────────────────────────────
/** An admin-defined field rendered on every record of a given type. */
export const customFieldSchema = z
    .object({
    id: z.string().min(1),
    label: z.string().trim().min(1).max(80),
    type: z.enum(['text', 'number', 'date', 'select', 'checkbox', 'url', 'email']),
    required: z.boolean().optional(),
    options: z.array(z.string().trim().min(1)).optional(),
    /**
     * Archived rather than deleted: records still hold values keyed by this id,
     * and deleting the definition would render them as orphaned JSON nobody can
     * read. Archiving hides it from new records and keeps old ones legible.
     */
    isArchived: z.boolean().optional(),
})
    .strict();
/** A named payment term. `netDays` drives the due date on a document. */
export const paymentTermSchema = z
    .object({
    id: z.string().min(1),
    label: z.string().trim().min(1).max(60),
    netDays: z.number().int().min(0).max(365),
    isDefault: z.boolean().optional(),
})
    .strict();
export const DEFAULT_PAYMENT_TERMS = [
    { id: 'due_on_receipt', label: 'Due on receipt', netDays: 0, isDefault: true },
    { id: 'net_15', label: 'Net 15', netDays: 15 },
    { id: 'net_30', label: 'Net 30', netDays: 30 },
    { id: 'net_45', label: 'Net 45', netDays: 45 },
    { id: 'net_60', label: 'Net 60', netDays: 60 },
];
// ── Letterhead and payment details: what prints on every document ─────────
/**
 * How the book presents itself on a PDF.
 *
 * The letterhead's TEXT - legal name, address, email, phone, website - lives on
 * the `orgs` row, because it existed there long before anything printed it.
 * What is here is the part that has no column: the signature image, whose
 * name goes under it, whether the logo prints at all, and the accent colour.
 *
 * `signatureFileId` names a `files` row uploaded as `ownerType=org`,
 * `role=signature`; the API checks it belongs to this org before it is
 * stored, the same way `logoFileId` on the org row is checked. Null clears.
 */
export const letterheadSettingsSchema = z
    .object({
    signatureFileId: z.string().trim().max(36).nullable().optional(),
    /** Printed under the signature line - "For Acme Pvt Ltd" or a person's name. */
    signatoryName: z.string().trim().max(120).optional(),
    /**
     * Default TRUE: a person who uploads a logo wants it on the invoice, and a
     * switch that defaults off is a support ticket. Off is for a book whose
     * logo exists for the app switcher and not for the customer.
     */
    showLogo: z.boolean().default(true),
    /** The title and table-header colour. Falls back to the book's own colour. */
    accentColour: colourSchema.optional(),
    /** One line under the name: "Chartered Accountants", "Since 1998". */
    tagline: z.string().trim().max(160).optional(),
})
    .strict();
/**
 * The PAYMENT DETAILS block at the foot of an invoice.
 *
 * Every field optional and every field free text, because Indian bank
 * particulars do not fit a stricter shape - an IFSC is 11 characters, but a
 * book outside India prints a SWIFT or an IBAN in the same slot, and the only
 * thing worse than an unvalidated account number is a rejected real one.
 * The block prints only the fields that are set, in this order.
 */
export const paymentDetailsSchema = z
    .object({
    bankName: z.string().trim().max(120).optional(),
    accountName: z.string().trim().max(160).optional(),
    accountNumber: z.string().trim().max(40).optional(),
    ifsc: z.string().trim().toUpperCase().max(20).optional(),
    /** For a customer paying from abroad. 8 or 11 characters, upper-cased. */
    swift: z.string().trim().toUpperCase().max(11).optional(),
    branch: z.string().trim().max(120).optional(),
    upiId: z.string().trim().max(80).optional(),
})
    .strict();
export const orgSettingsSchema = z
    .object({
    tax: taxSettingsSchema.optional(),
    /**
     * Letters of Undertaking, one per financial year (CGST Rule 96A). Top
     * level rather than inside `tax`, because `tax` is replaced whole on
     * save: the GST card sending its own fields must not wipe the LUTs.
     */
    luts: lutsSchema.default([]),
    /** Everything is STORED in UTC; this is display only. */
    timezone: z.string().trim().min(1).default('Asia/Kolkata'),
    baseCurrency: currencyCodeSchema.default('INR'),
    /** First month of the financial year. 4 = April, the Indian default. */
    fiscalYearStartMonth: z.number().int().min(1).max(12).default(4),
    paymentTerms: z.array(paymentTermSchema).default(DEFAULT_PAYMENT_TERMS),
    customerFields: z.array(customFieldSchema).default([]),
    vendorFields: z.array(customFieldSchema).default([]),
    itemFields: z.array(customFieldSchema).default([]),
    invoiceFields: z.array(customFieldSchema).default([]),
    expenseFields: z.array(customFieldSchema).default([]),
    /** Printed under the totals on every document unless one overrides it. */
    defaultTerms: z.string().max(4000).optional(),
    defaultNotes: z.string().max(4000).optional(),
    /** Where invoice emails and customer replies go. */
    replyToEmail: z.string().trim().email().optional(),
    /** What prints at the head and foot of every PDF. See the two schemas above. */
    letterhead: letterheadSettingsSchema.optional(),
    paymentDetails: paymentDetailsSchema.optional(),
})
    .strict();
/** What a brand-new org starts with. */
export const DEFAULT_ORG_SETTINGS = orgSettingsSchema.parse({});
/**
 * The ONLY supported way to read `orgs.settings_json`.
 *
 * Parses a string as well as an object, because on MariaDB Drizzle's `json()`
 * is LONGTEXT and mysql2 hands back the raw string. Iterating an un-parsed
 * string is the specific corruption FormDesk hit in production - and it cannot
 * reproduce on local MySQL 8, where the same column is native JSON and comes
 * back parsed. Centralising the parse is the only defence that survives someone
 * writing a new read path in six months.
 *
 * Never throws: an org whose settings blob is corrupt must still be able to log
 * in and fix it, so a bad value degrades to the defaults rather than a 500.
 */
export function parseOrgSettings(raw) {
    let value = raw;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed)
            return DEFAULT_ORG_SETTINGS;
        try {
            value = JSON.parse(trimmed);
        }
        catch {
            // Malformed JSON in the column. Defaults keep the org usable; the admin
            // can re-save settings to repair it.
            return DEFAULT_ORG_SETTINGS;
        }
    }
    if (value == null || typeof value !== 'object')
        return DEFAULT_ORG_SETTINGS;
    const parsed = orgSettingsSchema.safeParse(value);
    return parsed.success ? parsed.data : DEFAULT_ORG_SETTINGS;
}
/** Is GST actually on AND configured enough to price a document? */
export function gstIsActive(settings) {
    return settings.tax?.regime === 'gst' && !!settings.tax.stateCode;
}
//# sourceMappingURL=org-settings.js.map